import { useReactiveVar } from '@apollo/client';
import {
  FeatureFlagProvider,
  FeatureFlagsController,
  type FeatureFlagConfig,
} from '@seek/candidate-feature-flag';
import { metrics } from '@seek/metrics-js';
import { Box, useThemeName } from 'braid-design-system';
import Cookies from 'js-cookie';
import { Fragment, useEffect, type ReactNode } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';

import { FooterContainer } from 'src/components/HeaderFooter/FooterContainer';
import { HeaderContainer } from 'src/components/HeaderFooter/HeaderContainer';
import { CreateResume } from 'src/components/Profile/CreateResume/CreateResume';
import { Profile } from 'src/components/Profile/Profile';
import { ProfileLoadError } from 'src/components/Profile/ProfileLoadError/ProfileLoadError';
import { useCandidateId } from 'src/components/Profile/data';
import { usePage } from 'src/components/Router/usePage';
import { configureTrackJs } from 'src/modules/error-logger';
import { launchDarklyExperiments } from 'src/modules/experiments/LaunchDarklyExperiments.ts';
import { useWindowHistory } from 'src/modules/hooks/useWindowHistory';

import { NUM_FAILED_QUERIES_TO_TRIGGER_ERROR_PAGE } from '../consts';
import { failedQueries } from '../modules/apollo/apolloState.ts';

import { InfoBanner } from './InfoBanner';
import { useGetUserDetails } from './analytics';

const SetComputedTheme = () => {
  const computedTheme = useThemeName();

  useEffect(() => {
    window.APP_CONFIG.theme = computedTheme;
  }, [computedTheme]);

  return <></>;
};

const ProfileApp = () => {
  const page = usePage();
  const { candidateId } = useCandidateId();
  const failedQueriesValue = useReactiveVar(failedQueries);
  const failedQueriesCount = Object.keys(failedQueriesValue).length;
  const tooManyErrors =
    failedQueriesCount >= NUM_FAILED_QUERIES_TO_TRIGGER_ERROR_PAGE;

  useEffect(() => metrics.count('first-render'), []);

  useEffect(() => {
    if (candidateId) {
      configureTrackJs(candidateId);
    }
  }, [candidateId]);

  // Reset scroll on page change (but not card or id)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  useGetUserDetails();

  return (
    <>
      <HeaderContainer />
      {tooManyErrors ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="spaceBetween"
          style={{ minHeight: '90vh' }}
        >
          <ProfileLoadError />
          <FooterContainer />
        </Box>
      ) : (
        <Fragment>
          <SetComputedTheme />
          <Outlet />
          <FooterContainer />
        </Fragment>
      )}
    </>
  );
};

const Layout = ({ children }: { children?: ReactNode }) => (
  <>
    <InfoBanner />
    {children ? children : <Outlet />}
  </>
);

export const App = () => {
  useWindowHistory();

  const solId = Cookies.get('sol_id');
  const featureFlagDebugger = Cookies.get('feature_flag_debugger') === 'true';

  // NOTE: Order is important, most specific to least specific
  return (
    <FeatureFlagProvider
      featureFlagsConfig={[] as FeatureFlagConfig[]}
      experimentsConfig={launchDarklyExperiments}
      solId={solId || ''}
      debug={featureFlagDebugger}
      shouldEnableOverride
      customContext={{
        applicationContext: {
          key: solId || '',
          namespace: 'Public Profile',
          build: '1.0.0',
          version: '1.0.0',
        },
      }}
    >
      <Routes>
        <Route path="/:lang?/profile" element={<Layout />}>
          <Route path="me" element={<ProfileApp />}>
            <Route
              path="create-resume/:card?/:id?"
              element={<CreateResume />}
            />
            <Route path=":card?/:id?" element={<Profile />} />
          </Route>
        </Route>
      </Routes>
      <FeatureFlagsController />
    </FeatureFlagProvider>
  );
};
